/**
 * DocSignatureLab - the Document Editor's Signature Lab
 * (parity pass, 2026-11-05), mirroring the Check Studio's Lab:
 *  - DRAW on a touch/mouse pad (the phase-1 pointer pad, larger);
 *  - UPLOAD a signature image - it is cleaned and inked by the
 *    shared derivation (SignatureExtractorEngine via
 *    utils/signatureInk), never placed raw in its white box;
 *  - TYPE your name in a script face, rasterized in the ink.
 * One ink control is shared by the Lab, the field pads and the
 * Library tab: presets, a custom colour, and "Keep original
 * colour" - the Check Studio's ink system, values identical.
 *
 * What the Lab hands back is always a (derived, raw) pair: the
 * derived image is what the document shows; the raw source is
 * what later ink picks re-derive from, so a placed signature
 * recolours when the ink changes - the promise the Check Studio
 * shipped. Typed signatures also hand back their provenance so
 * they re-render from text instead of tinting a raster.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
    SIGNATURE_INK_PRESETS, normalizeSignatureInk, deriveSignatureImage,
    renderTypedSignature, SIGNATURE_TYPE_FACES, TypedSignatureSource
} from '../utils/signatureInk';

export interface SignatureLabResult {
    /** The image to place on the document. */
    derived: string;
    /** The raw source later ink picks re-derive from. */
    raw: string;
    /** Present when the signature was typed. */
    typed?: TypedSignatureSource;
    /** Library/display name for the produced signature. */
    name: string;
}

/** The one signature-ink control: preset dots, custom colour,
 * and the Keep-original toggle. Used by the Lab and the editor's
 * Library tab so both drive the same ink state. */
export const SignatureInkControls: React.FC<{
    inkColor: string;
    inkOriginal: boolean;
    onInkChange: (color: string, original: boolean) => void;
    dark?: boolean;
}> = ({ inkColor, inkOriginal, onInkChange, dark }) => {
    const label = dark ? '#CBD5E1' : '#6B7280';
    return (
        <div>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                {SIGNATURE_INK_PRESETS.map(ink => {
                    const selected = !inkOriginal && inkColor.toLowerCase() === ink.val;
                    return (
                        <button
                            key={ink.val}
                            type="button"
                            title={ink.name}
                            aria-label={`Signature ink ${ink.name}`}
                            aria-pressed={selected}
                            onClick={() => onInkChange(ink.val, false)}
                            style={{
                                width: '24px', height: '24px', borderRadius: '50%', backgroundColor: ink.val, cursor: 'pointer',
                                border: selected ? '2px solid #3498db' : '2px solid rgba(128,128,128,0.35)'
                            }}
                        />
                    );
                })}
                <input
                    type="color"
                    aria-label="Custom signature ink colour"
                    value={normalizeSignatureInk(inkColor)}
                    onChange={e => onInkChange(e.target.value, false)}
                    style={{ width: '28px', height: '24px', padding: 0, border: '1px solid #cbd5e1', borderRadius: '6px', background: 'none', cursor: 'pointer' }}
                />
                <span style={{ fontSize: '10px', color: label, fontFamily: 'monospace' }}>{inkOriginal ? 'original' : inkColor.toLowerCase()}</span>
            </div>
            <button
                type="button"
                aria-pressed={inkOriginal}
                onClick={() => onInkChange(inkColor, !inkOriginal)}
                style={{
                    marginTop: '6px', padding: '4px 8px', fontSize: '10px', fontWeight: '600', borderRadius: '6px', cursor: 'pointer',
                    backgroundColor: inkOriginal ? 'rgba(249,115,22,0.15)' : 'transparent',
                    color: inkOriginal ? '#EA580C' : label,
                    border: inkOriginal ? '1px solid #F97316' : `1px solid ${dark ? 'rgba(255,255,255,0.12)' : '#D1D5DB'}`
                }}
            >
                🎨 {inkOriginal ? 'Keeping the original colour' : 'Keep original colour'}
            </button>
        </div>
    );
};

interface DocSignatureLabProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (result: SignatureLabResult) => void;
    inkColor: string;
    inkOriginal: boolean;
    onInkChange: (color: string, original: boolean) => void;
}

const readFileAsDataUrl = (file: File): Promise<string> => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = () => reject(r.error || new Error('Could not read the file'));
    r.readAsDataURL(file);
});

export const DocSignatureLab: React.FC<DocSignatureLabProps> = ({ isOpen, onClose, onSave, inkColor, inkOriginal, onInkChange }) => {
    const [mode, setMode] = useState<'draw' | 'upload' | 'type'>('draw');
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const drawingRef = useRef(false);
    const drewRef = useRef(false);
    const lastRef = useRef<{ x: number; y: number } | null>(null);
    const [uploadRaw, setUploadRaw] = useState<string | null>(null);
    const [uploadName, setUploadName] = useState('Signature');
    const [typedText, setTypedText] = useState('');
    const [typedFace, setTypedFace] = useState('fountain');
    const [busy, setBusy] = useState(false);
    const [note, setNote] = useState<string | null>(null);

    // The pad's ink: a chosen ink draws in that colour; in
    // Keep-original mode the drawing's own colour is neutral
    // black-blue - it IS the original once drawn.
    const padInk = inkOriginal ? '#111827' : normalizeSignatureInk(inkColor);

    useEffect(() => {
        if (!isOpen) return;
        setMode('draw'); setUploadRaw(null); setNote(null); drewRef.current = false;
    }, [isOpen]);

    if (!isOpen) return null;

    const point = (e: React.PointerEvent) => {
        const canvas = canvasRef.current!;
        const r = canvas.getBoundingClientRect();
        return {
            x: (e.clientX - r.left) * (canvas.width / Math.max(1, r.width)),
            y: (e.clientY - r.top) * (canvas.height / Math.max(1, r.height))
        };
    };

    const strokeTo = (e: React.PointerEvent) => {
        const canvas = canvasRef.current;
        if (!canvas || !drawingRef.current) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const p = point(e);
        const last = lastRef.current || p;
        ctx.beginPath();
        ctx.moveTo(last.x, last.y);
        ctx.lineTo(p.x, p.y);
        ctx.strokeStyle = padInk;
        ctx.lineWidth = 2.4;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.stroke();
        lastRef.current = p;
        drewRef.current = true;
    };

    const clearPad = () => {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
        drewRef.current = false;
    };

    const handleSave = async () => {
        if (busy) return;
        setNote(null);
        if (mode === 'draw') {
            const canvas = canvasRef.current;
            if (!canvas || !drewRef.current) { setNote('Draw your signature first - the pad is empty.'); return; }
            let raw = '';
            try { raw = canvas.toDataURL('image/png'); } catch { /* canvas unavailable */ }
            if (!raw) { setNote('The drawing could not be captured in this browser.'); return; }
            // The drawing is already isolated strokes in the pad
            // ink - it places as drawn, and the raw is stored so a
            // later ink pick re-derives through the shared pass.
            onSave({ derived: raw, raw, name: 'Drawn signature' });
            onClose();
            return;
        }
        if (mode === 'upload') {
            if (!uploadRaw) { setNote('Choose a signature image first.'); return; }
            setBusy(true);
            try {
                const derived = await deriveSignatureImage(uploadRaw, inkColor, inkOriginal);
                onSave({ derived, raw: uploadRaw, name: uploadName });
                onClose();
            } finally { setBusy(false); }
            return;
        }
        // type
        const text = typedText.trim();
        if (!text) { setNote('Type your name first.'); return; }
        const ink = inkOriginal ? '#1b2440' : inkColor;
        const raster = renderTypedSignature(text, typedFace, ink);
        if (!raster) { setNote('The typed signature could not be rendered in this browser.'); return; }
        onSave({
            derived: raster,
            raw: raster,
            typed: { kind: 'typed', text, face: typedFace, ink: normalizeSignatureInk(ink) },
            name: text
        });
        onClose();
    };

    const tabBtn = (m: 'draw' | 'upload' | 'type', label: string) => (
        <button
            type="button"
            onClick={() => setMode(m)}
            style={{
                padding: '6px 12px', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600',
                background: mode === m ? '#3B82F6' : '#F3F4F6', color: mode === m ? '#FFF' : '#374151', border: '1px solid #E5E7EB'
            }}
        >
            {label}
        </button>
    );

    return (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' }}>
            <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '12px', width: '560px', maxWidth: '94vw', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <h3 style={{ margin: 0, fontSize: '18px', color: '#1a3a5c' }}>Signature Lab</h3>
                    <button type="button" onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '20px', color: '#666' }}>&times;</button>
                </div>

                <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
                    {tabBtn('draw', '✏️ Draw')}
                    {tabBtn('upload', '📤 Upload')}
                    {tabBtn('type', '⌨️ Type')}
                </div>

                <div style={{ marginBottom: '14px' }}>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>Signature ink</label>
                    <SignatureInkControls inkColor={inkColor} inkOriginal={inkOriginal} onInkChange={onInkChange} />
                </div>

                {mode === 'draw' && (
                    <div>
                        <div style={{ border: '2px dashed #cbd5e1', borderRadius: '8px', overflow: 'hidden', background: '#FFF' }}>
                            <canvas
                                ref={canvasRef}
                                data-signature-lab-pad
                                width={500}
                                height={180}
                                style={{ width: '100%', display: 'block', cursor: 'crosshair', touchAction: 'none' }}
                                onPointerDown={e => {
                                    try { (e.target as HTMLElement).setPointerCapture(e.pointerId); } catch { /* best-effort */ }
                                    drawingRef.current = true;
                                    lastRef.current = point(e);
                                    strokeTo(e);
                                }}
                                onPointerMove={strokeTo}
                                onPointerUp={() => { drawingRef.current = false; lastRef.current = null; }}
                                onPointerCancel={() => { drawingRef.current = false; lastRef.current = null; }}
                            />
                        </div>
                        <button type="button" onClick={clearPad} style={{ marginTop: '8px', padding: '6px 12px', borderRadius: '6px', border: '1px solid #E5E7EB', background: '#F9FAFB', cursor: 'pointer', fontSize: '12px' }}>Clear</button>
                    </div>
                )}

                {mode === 'upload' && (
                    <div>
                        <input
                            type="file"
                            accept="image/*"
                            aria-label="Upload a signature image"
                            onChange={async e => {
                                const file = e.target.files?.[0];
                                e.target.value = '';
                                if (!file) return;
                                try {
                                    const raw = await readFileAsDataUrl(file);
                                    setUploadRaw(raw);
                                    setUploadName(file.name || 'Signature');
                                } catch { setNote('That file could not be read.'); }
                            }}
                            style={{ fontSize: '12px' }}
                        />
                        {uploadRaw && (
                            <div style={{ marginTop: '10px', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '8px', background: '#FFF' }}>
                                <img src={uploadRaw} alt="Signature to clean and place" style={{ maxWidth: '100%', maxHeight: '120px', display: 'block', margin: 'auto' }} />
                                <p style={{ margin: '6px 0 0', fontSize: '11px', color: '#6B7280', textAlign: 'center' }}>On Save it is isolated from its background and set in the ink above.</p>
                            </div>
                        )}
                    </div>
                )}

                {mode === 'type' && (
                    <div>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>Your name, as you sign it</label>
                        <input
                            type="text"
                            aria-label="Name to sign"
                            value={typedText}
                            onChange={e => setTypedText(e.target.value)}
                            placeholder="e.g. Princewill Peters"
                            style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #E5E7EB', fontSize: '13px', marginBottom: '8px' }}
                        />
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>Script</label>
                        <select
                            aria-label="Signature script"
                            value={typedFace}
                            onChange={e => setTypedFace(e.target.value)}
                            style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #E5E7EB', fontSize: '13px' }}
                        >
                            {SIGNATURE_TYPE_FACES.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                        </select>
                        {typedText.trim() && (
                            <div style={{ marginTop: '10px', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '10px', textAlign: 'center', fontSize: '30px', color: inkOriginal ? '#1b2440' : normalizeSignatureInk(inkColor), fontFamily: SIGNATURE_TYPE_FACES.find(f => f.id === typedFace)?.stack }}>
                                {typedText}
                            </div>
                        )}
                    </div>
                )}

                {note && <div style={{ marginTop: '10px', padding: '8px 10px', borderRadius: '8px', background: '#FEF2F2', color: '#991B1B', fontSize: '12px' }}>{note}</div>}

                <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
                    <button type="button" onClick={onClose} style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid #E5E7EB', background: '#F9FAFB', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#374151' }}>Cancel</button>
                    <button type="button" onClick={handleSave} disabled={busy} style={{ flex: 1, padding: '10px', borderRadius: '8px', border: 'none', background: '#3B82F6', cursor: busy ? 'wait' : 'pointer', fontSize: '13px', fontWeight: '600', color: '#FFF' }}>{busy ? 'Working…' : 'Use this signature'}</button>
                </div>
            </div>
        </div>
    );
};

export default DocSignatureLab;
